import { productPriceInventory } from 'api/mdl22'
import { useDispatch, useSelector } from 'react-redux'
import {
  addBatchToCart,
  emptyCart,
  getPayloadProduct,
  isLoadingProduct,
} from 'redux/actions/cartActions'
import {
  fethCheckFinalPrice,
  processWarranties,
  buildFinalProductsInOrder,
} from './checkFinalPriceUtils'

export const useCheckFinalPrice = () => {
  const dispatch = useDispatch()
  const company = useSelector(state => state.company)
  const cart = useSelector(state => state.cart.cart)

  const checkFinalPrice = async ({
    prods,
    customerId,
    newCompany,
    isClone,
  } = {}) => {
    dispatch(isLoadingProduct({ isLoadingAllProducts: true }))
    const allProducts = prods || cart
    if (!allProducts.length) {
      return dispatch(isLoadingProduct({ isLoadingAllProducts: false }))
    }

    // Separate warranty and regular products
    // Also detect warranty items by WarrantyProductId since backend may return IsExtendedWarranty: null
    const isWarrantyLine = p =>
      p.IsExtendedWarranty || p.WarrantyProductId != null
    const productList = allProducts.filter(p => !isWarrantyLine(p))
    const warrantyProducts = allProducts.filter(p => isWarrantyLine(p))

    if (!productList.length && !warrantyProducts.length) {
      return dispatch(isLoadingProduct({ isLoadingProduct: false }))
    }

    const companyCode = newCompany?.CompanyCode || company.CompanyCode
    const companyId = newCompany?.CompanyId || company.CompanyId
    const WarehouseId = newCompany?.WarehouseId || company.WarehouseId

    const batches = []
    for (let i = 0; i < productList.length; i += 99) {
      batches.push(productList.slice(i, i + 99))
    }

    // Process warranties once — not once per batch
    const processedWarranties = processWarranties(warrantyProducts, newCompany)

    // Snapshot the cart before clearing so we can restore on API failure
    const cartSnapshot = allProducts.map(p => ({ product: p, qty: p.Quantity }))

    dispatch(emptyCart())

    try {
      // Fetch all batches in parallel instead of sequentially
      const batchResults = await Promise.all(
        batches.map(async batch => {
          const ProductIds = batch.map(product => product.ProductId)
          const combinedPayload = batch.map(prod => ({
            product: prod,
            qty: prod.Quantity,
          }))

          const priceInventoryPayload = {
            CompanyId: companyId,
            CompanyCode: companyCode,
            WarehouseId,
            ProductIds,
            UoMId: null,
            PriceListId: null,
          }

          // Both API calls for this batch run in parallel
          const [response, processedProducts] = await Promise.all([
            productPriceInventory(priceInventoryPayload),
            fethCheckFinalPrice(combinedPayload, customerId, newCompany),
          ])

          return {
            updatedProducts: response.data?.Data || [],
            processedProducts: processedProducts || [],
          }
        })
      )

      const allUpdatedProducts = batchResults.flatMap(r => r.updatedProducts)
      const allProcessedProducts = batchResults.flatMap(
        r => r.processedProducts
      )

      const orderedProducts = buildFinalProductsInOrder(
        allProducts,
        allProcessedProducts,
        processedWarranties
      )

      // Build a map of ProductId -> new price from the fetched products
      const parentPriceMap = new Map()
      allProcessedProducts?.forEach(p => {
        const productId = p.selectList?.ProductId
        if (productId != null && p.Price != null)
          parentPriceMap.set(productId, p.Price)
      })

      orderedProducts?.forEach((orderedProduct, index) => {
        const updatedProduct = allUpdatedProducts?.find(
          up => up.ProductId === orderedProduct.selectList.ProductId
        )
        if (updatedProduct) {
          orderedProducts[index].payload.product.Cost = updatedProduct.Cost
          orderedProducts[index].payload.product.AvgCost =
            updatedProduct.AvgCost
          orderedProducts[index].payload.product.AvgPrice =
            updatedProduct.AvgPrice
        }
      })

      // Build all cart items and dispatch in a single state update
      const cartItems = orderedProducts.map(prod => {
        if (
          prod.payload?.product?.IsExtendedWarranty ||
          prod.payload?.product?.WarrantyProductId
        ) {
          const warrantyItem = prod.payload.product
          const parentPrice = parentPriceMap.get(warrantyItem.WarrantyProductId)
          let updatedWarrantyItem = {
            ...warrantyItem,
            IsExtendedWarranty: true,
          }

          if (parentPrice != null && warrantyItem.WarrantyPercent != null) {
            const newPrice = (parentPrice * warrantyItem.WarrantyPercent) / 100
            const qty = warrantyItem.Quantity || 1
            const taxRate = warrantyItem.TaxCategoryValue || 0
            const newAmount = newPrice * qty
            const newTax = newAmount * taxRate
            updatedWarrantyItem = {
              ...updatedWarrantyItem,
              Price: newPrice,
              BasePrice: newPrice,
              FinalPrice: newPrice,
              Amount: newAmount,
              AmountWithTax: newAmount + newTax,
              Tax: newTax,
            }
          }

          return {
            product: updatedWarrantyItem,
            qty: warrantyItem.Quantity || 1,
          }
        }
        return {
          product: getPayloadProduct(
            prod.selectList,
            prod.payload,
            prod.warehouse,
            prod.Quantity,
            prod.Price,
            isClone
          ),
          qty: prod.qty,
        }
      })

      dispatch(addBatchToCart(cartItems))
    } catch (error) {
      // Restore the cart to its state before the API calls so the user
      // doesn't lose their products when the network request fails
      dispatch(addBatchToCart(cartSnapshot))
      throw error
    } finally {
      // Always clear the loading flag regardless of success or failure
      dispatch(isLoadingProduct({ isLoadingAllProducts: false }))
    }
  }

  return { checkFinalPrice }
}
